/**
 * SubmissionDetail — shared read-only view of a submission.
 *
 * Used in:
 *  - TestDetails.js  (student's own problem card)
 *  - ScoreMatrix.js  (feedback modal for both student and grader views)
 *
 * Props:
 *  submission      — the Submission document (populated)
 *  maxScore        — max score per problem (default 7)
 *  onViewFile      — (submissionId, fileIndex, fileName) => void
 *  currentUser     — logged-in user object (for role-based display)
 */
import React, { useState } from 'react';
import API_BASE_URL from '../../config/api';
import LaTeXRenderer from '../LaTeXRenderer';

const SubmissionDetail = ({ submission, maxScore = 7, onViewFile, currentUser, hideFeedback = false }) => {
  const [ocrText, setOcrText] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [showOcr, setShowOcr] = useState(false);

  const [aiGradeText, setAiGradeText] = useState(null);
  const [aiGradeLoading, setAiGradeLoading] = useState(false);
  const [showAiGrade, setShowAiGrade] = useState(false);

  if (!submission) return null;

  const fetchOcr = async () => {
    if (ocrText) return;
    setOcrLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/submissions/${submission._id}/ocr`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setOcrText(res.ok ? await res.text() : '[Failed to load OCR text]');
    } catch {
      setOcrText('[Error loading OCR text]');
    } finally {
      setOcrLoading(false);
    }
  };

  const fetchAiGrade = async () => {
    if (aiGradeText) return;
    setAiGradeLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/submissions/${submission._id}/ai-grade`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setAiGradeText(res.ok ? await res.text() : '[Failed to load AI feedback]');
    } catch {
      setAiGradeText('[Error loading AI feedback]');
    } finally {
      setAiGradeLoading(false);
    }
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  const showOcrButton =
    submission.ocrStatus &&
    submission.ocrStatus !== 'skipped' &&
    submission.ocrStatus !== 'pending';

  // ── Processing Status Banner ──
  // OCR only applies to uploaded files — never show for text-only submissions
  const hasFiles = submission.solutionFiles && submission.solutionFiles.length > 0;
  const showOcrProgress =
    hasFiles &&
    submission.ocrStatus &&
    submission.ocrStatus !== 'completed' &&
    submission.ocrStatus !== 'skipped';

  const showAiProgress =
    submission.aiGradeStatus &&
    submission.aiGradeStatus !== 'completed';

  // Text-only submission that was too short for AI grading
  // Covers both ocrStatus='skipped' and ocrStatus='pending' (backend may not have processed yet)
  const textTooShort =
    !hasFiles &&
    !submission.aiGradeStatus &&
    submission.submissionText &&
    submission.submissionText.trim().length < 50;

  const ocrStatusLabel = {
    pending:    { icon: '⏳', text: 'OCR queued',        color: '#856404', bg: '#fff3cd', border: '#ffc107' },
    processing: { icon: '🔄', text: 'Extracting text…',  color: '#004085', bg: '#cce5ff', border: '#b8daff' },
    failed:     { icon: '❌', text: 'OCR failed (will retry)', color: '#721c24', bg: '#f8d7da', border: '#f5c6cb' },
  };

  const aiStatusLabel = {
    pending:    { icon: '⏳', text: 'AI grading queued',       color: '#856404', bg: '#fff3cd', border: '#ffc107' },
    processing: { icon: '🤖', text: 'AI analyzing solution…',  color: '#004085', bg: '#cce5ff', border: '#b8daff' },
    failed:     { icon: '❌', text: 'AI grading failed (will retry)', color: '#721c24', bg: '#f8d7da', border: '#f5c6cb' },
  };

  return (
    <div>
      {/* ── OCR / AI Grading Progress Badges ── */}
      {(showOcrProgress || showAiProgress || textTooShort) && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
          {textTooShort && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              padding: '4px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: '500',
              color: '#856404', backgroundColor: '#fff3cd', border: '1px solid #ffc107',
            }}>
              ⚠️ Text too short for AI feedback ({submission.submissionText.trim().length}/50 chars) — AI grading skipped
            </span>
          )}
          {showOcrProgress && ocrStatusLabel[submission.ocrStatus] && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              padding: '4px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: '500',
              color: ocrStatusLabel[submission.ocrStatus].color,
              backgroundColor: ocrStatusLabel[submission.ocrStatus].bg,
              border: `1px solid ${ocrStatusLabel[submission.ocrStatus].border}`,
            }}>
              {ocrStatusLabel[submission.ocrStatus].icon} {ocrStatusLabel[submission.ocrStatus].text}
            </span>
          )}
          {showAiProgress && aiStatusLabel[submission.aiGradeStatus] && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              padding: '4px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: '500',
              color: aiStatusLabel[submission.aiGradeStatus].color,
              backgroundColor: aiStatusLabel[submission.aiGradeStatus].bg,
              border: `1px solid ${aiStatusLabel[submission.aiGradeStatus].border}`,
            }}>
              {aiStatusLabel[submission.aiGradeStatus].icon} {aiStatusLabel[submission.aiGradeStatus].text}
            </span>
          )}
        </div>
      )}

      {/* ── Solution Files ── */}
      {submission.solutionFiles && submission.solutionFiles.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <strong>Solution Files:</strong>
          <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {submission.solutionFiles.map((file, index) => (
              <button
                key={index}
                onClick={() => onViewFile && onViewFile(submission._id, index, file.originalname)}
                className="btn btn-outline"
                style={{ fontSize: '12px', padding: '4px 8px' }}
              >
                📎 {file.originalname}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Text Submission ── */}
      {submission.submissionText && (
        <div style={{ marginBottom: '12px' }}>
          <strong>Text Submission:</strong>
          <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '14px' }}>
            <LaTeXRenderer text={submission.submissionText} />
          </div>
        </div>
      )}

      {/* ── OCR Extracted Text (file submissions only) ── */}
      {showOcrButton && (
        <div style={{ marginBottom: '12px' }}>
          <button
            onClick={async () => {
              if (!showOcr && submission.ocrStatus === 'completed') await fetchOcr();
              setShowOcr(!showOcr);
            }}
            className="btn btn-outline"
            style={{ fontSize: '12px', padding: '4px 10px' }}
          >
            {submission.ocrStatus === 'completed'
              ? (showOcr ? '🔽 Hide OCR Text' : '🤖 Show AI-Extracted Text')
              : submission.ocrStatus === 'processing'
                ? '⏳ Extracting text...'
                : submission.ocrStatus === 'failed'
                  ? '❌ Text extraction failed'
                  : '⏳ Processing...'}
          </button>
          {showOcr && (
            <div style={{ marginTop: '8px', padding: '16px', backgroundColor: '#f0f7ff', borderRadius: '6px', border: '1px solid #b3d9ff', fontSize: '14px' }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '8px' }}>
                🤖 AI-extracted text from your uploaded file — may contain errors
              </div>
              {ocrLoading
                ? <div style={{ color: '#666' }}>Loading extracted text...</div>
                : ocrText
                  ? <div style={{ whiteSpace: 'pre-wrap' }}><LaTeXRenderer text={ocrText} /></div>
                  : <div style={{ color: '#666' }}>No extracted text available</div>}
            </div>
          )}
        </div>
      )}

      {/* ── AI Feedback ── */}
      {submission.aiGradeStatus && submission.aiGradeStatus !== 'pending' && (
        <div style={{ marginBottom: '12px' }}>
          <button
            onClick={async () => {
              if (!showAiGrade && submission.aiGradeStatus === 'completed') await fetchAiGrade();
              setShowAiGrade(!showAiGrade);
            }}
            className="btn btn-outline"
            style={{ fontSize: '12px', padding: '4px 10px' }}
          >
            {submission.aiGradeStatus === 'completed'
              ? (showAiGrade
                  ? '🔽 Hide AI Feedback'
                  : `🤖 Show AI Feedback${submission.aiGradeScore != null ? ` (${submission.aiGradeScore}/7)` : ''}`)
              : submission.aiGradeStatus === 'processing'
                ? '⏳ AI analyzing your solution...'
                : submission.aiGradeStatus === 'failed'
                  ? '❌ AI feedback unavailable'
                  : '⏳ Processing...'}
          </button>
          {showAiGrade && (
            <div style={{ marginTop: '8px', padding: '16px', backgroundColor: '#fff8e1', borderRadius: '6px', border: '1px solid #ffe082', fontSize: '14px' }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '8px' }}>
                🤖 AI-generated feedback — use as a learning guide, final grading is done by human graders
              </div>
              {submission.aiGradeScore != null && (
                <div style={{ display: 'inline-block', padding: '4px 12px', backgroundColor: '#e8f5e8', borderRadius: '4px', fontSize: '16px', fontWeight: 'bold', color: '#155724', marginBottom: '12px' }}>
                  AI Score: {submission.aiGradeScore}/7
                </div>
              )}
              {aiGradeLoading
                ? <div style={{ color: '#666' }}>Loading AI feedback...</div>
                : aiGradeText
                  ? <div style={{ whiteSpace: 'pre-wrap' }}><LaTeXRenderer text={aiGradeText} /></div>
                  : <div style={{ color: '#666' }}>No AI feedback available</div>}
            </div>
          )}
        </div>
      )}

      {/* ── Grader Feedback ── */}
      {!hideFeedback && submission.feedback && submission.feedback.length > 0 ? (
        <div>
          <strong>Grading Feedback ({submission.feedback.length} comment{submission.feedback.length !== 1 ? 's' : ''}):</strong>
          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {submission.feedback.map((feedback, index) => (
              <div key={index} style={{ padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d9ff' }}>
                {feedback.gradedBy && currentUser?.role === 'teacher' && (
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
                    <strong>Grader:</strong> {feedback.gradedBy.name}
                    {feedback.gradedAt && (
                      <span> • {formatDate(feedback.gradedAt)}</span>
                    )}
                  </div>
                )}
                {feedback.gradedAt && currentUser?.role !== 'teacher' && (
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
                    Reviewed on {formatDate(feedback.gradedAt)}
                  </div>
                )}
                <div style={{ marginBottom: '8px' }}>
                  <LaTeXRenderer text={feedback.text} />
                </div>
                {feedback.score != null && (
                  <div style={{ display: 'inline-block', padding: '4px 8px', backgroundColor: '#e8f5e8', borderRadius: '4px', fontSize: '14px', fontWeight: 'bold', color: '#155724' }}>
                    Score: {feedback.score}/{maxScore}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : !hideFeedback ? (
        <div style={{ color: '#666', fontSize: '14px' }}>
          {submission.status === 'graded'
            ? 'This submission has been graded but no detailed feedback was provided.'
            : 'No grader feedback yet. Check back later.'}
        </div>
      ) : null}
    </div>
  );
};

export default SubmissionDetail;